import { MONACO_SQL_LANGUAGE_TYPES } from "@hex/common";
import { editor as Editor, languages } from "monaco-editor";

import { useSelectCell } from "../../../hooks/cell/useSelectCell";
import { useOnMount } from "../../../hooks/useOnMount";
import { useDispatch } from "../../../redux/hooks";
import {
  setSearchActive,
  setSearchInput,
} from "../../../redux/slices/schemaTreeBrowserSlice";
import { useHexFlag } from "../../../util/useHexFlags";
import { useOpenDataBrowserSidebar } from "../../data/useOpenDataBrowserSidebar.js";

import {
  MagicCompletionData,
  useKernelCompletion,
} from "./KernelCompletionProvider";
import { PARAMETER_COMPLETION_PROVIDER } from "./ParameterCompletionProvider";
import { SQL_COMPLETION_PROVIDER } from "./SqlCompletionProvider";
import { useTypeaheadCompletion } from "./TypeaheadCompletionProvider";
import { useTrackInlineCompletionEventReviewedMutation } from "./TypeaheadCompletionProvider.generated";

import { registerSqlCompletionCommand } from "./sqlCompletionCommand";

// HACK HACK HACK prevent setting the language completions multiple times.
// this is in a seperate file to prevent webpack hot reload from
// resetting it.
let completionLoaded = false;

export const TRACK_TYPEAHEAD_COMPLETION_ACCEPTED_CMD =
  "trackTypeaheadCompletionAccepted";
const escapeRegex = (regex: string): string => {
  // Escape the value for the concatenated regexp.
  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Escaping
  return regex.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&");
};

export function useMonacoCompletion(): void {
  const openConnectionsSidebar = useOpenDataBrowserSidebar();
  const dispatch = useDispatch();
  const [trackInlineCompletionReviewed] =
    useTrackInlineCompletionEventReviewedMutation();
  const typeaheadV2Config = useHexFlag("typeahead-v-2");
  const kernelCompletion = useKernelCompletion();
  const typeaheadCompletion = useTypeaheadCompletion();
  const { selectCells } = useSelectCell();
  useOnMount(() => {
    if (!completionLoaded) {
      completionLoaded = true;
      for (const language of ["python", "r"]) {
        languages.registerCompletionItemProvider(language, kernelCompletion);
      }

      const parameterCompletionLanguages = [...MONACO_SQL_LANGUAGE_TYPES, "r"];
      for (const language of parameterCompletionLanguages) {
        languages.registerCompletionItemProvider(
          language,
          PARAMETER_COMPLETION_PROVIDER,
        );
      }

      Editor.registerCommand(
        "autoRename",
        (_accessor, { modelUri, newVariable, oldVariable }) => {
          const model = Editor.getModel(modelUri);
          if (model) {
            const value = model.getValue();
            // Not a proper replace, will also affect comments, strings, etc.
            // Just a hack for now but this is okay
            const newValue = value.replace(
              new RegExp(`${escapeRegex(oldVariable)}\\b`, "g"),
              newVariable,
            );
            model.setValue(newValue);
          }
        },
      );
      Editor.registerCommand("selectCell", (_accessor, { cellId }) => {
        selectCells(cellId);
      });
      registerSqlCompletionCommand();

      Editor.registerCommand(
        TRACK_TYPEAHEAD_COMPLETION_ACCEPTED_CMD,
        (_accessor, args: MagicCompletionData) => {
          if (typeaheadCompletion.lastCompletion != null) {
            void trackInlineCompletionReviewed({
              variables: {
                completionId: args.completionId,
                accepted: true,
              },
            });
            typeaheadCompletion.lastCompletion.accepted = true;
          }
        },
      );

      Editor.registerCommand(
        "viewTableInSchemaBrowser",
        (_accessor, { dataConnectionId, table }) => {
          openConnectionsSidebar(dataConnectionId);
          dispatch(
            setSearchActive({
              dataConnectionId,
              searchActive: true,
              showPinnedOnly: false,
            }),
          );
          dispatch(
            setSearchInput({
              dataConnectionId,
              searchInput: `table:"${table}"`,
            }),
          );
        },
      );
      for (const language of [...MONACO_SQL_LANGUAGE_TYPES]) {
        languages.registerCompletionItemProvider(
          language,
          SQL_COMPLETION_PROVIDER,
        );

        if (typeaheadV2Config.enabledSql) {
          languages.registerInlineCompletionsProvider(
            language,
            typeaheadCompletion,
          );
        }
      }

      // For python
      if (typeaheadV2Config.enabledPython) {
        languages.registerInlineCompletionsProvider(
          "python",
          typeaheadCompletion,
        );
      }
    }
  });
}
